// src/pages/Admin/create/deal/DealReview.tsx
import React from "react";
import { Env, ENV_COLORS } from "../../shared";
import { useAdminFlag } from "../../deals/featureFlags";
import { defaultRecurrenceEndDate, summarizeRecurrence } from "../shared/recurrence";
import { DealForm as DealFormState } from "./useDealFormState";

export function DealReview({ env, form }: { env: Env; form: DealFormState }) {
  const RECURRING_DEALS_ENABLED = useAdminFlag('recurring-deals-enabled', env);
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2">
        <span className={`inline-flex items-center px-3 py-1.5 rounded-lg border text-sm font-semibold ${ENV_COLORS[env].pill}`}>
          {env.toUpperCase()}
        </span>
        <span className="text-xs text-gray-500">environment</span>
      </div>

      <div className="p-4 bg-gray-800/50 border border-gray-700/50 rounded-xl space-y-2">
        <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Deal</h4>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          <span className="text-gray-500">Business</span><span className="text-white truncate">{form.business?.name}</span>
          <span className="text-gray-500">Title</span><span className="text-white">{form.title}</span>
          {form.description && <><span className="text-gray-500">Description</span><span className="text-white truncate">{form.description}</span></>}
        </div>
      </div>

      <div className="p-4 bg-gray-800/50 border border-gray-700/50 rounded-xl space-y-2">
        <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Schedule</h4>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          <span className="text-gray-500">Start</span><span className="text-white">{form.startTime}</span>
          <span className="text-gray-500">End</span><span className="text-white">{form.endTime}</span>
        </div>
      </div>

      <div className="p-4 bg-gray-800/50 border border-gray-700/50 rounded-xl space-y-2">
        <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Audience</h4>
        <div className="flex flex-wrap gap-2">
          {form.notificationAudience.map(a => (
            <span key={a} className="px-2.5 py-1 bg-violet-950/40 border border-violet-800/50 rounded-lg text-xs text-violet-300">{a}</span>
          ))}
        </div>
      </div>

      {RECURRING_DEALS_ENABLED && form.recurrenceEnabled && form.recurrenceDays.length > 0 && (
        <div className="p-4 bg-violet-950/20 border border-violet-800/40 rounded-xl space-y-1">
          <h4 className="text-xs font-semibold text-violet-300 uppercase tracking-wider">Recurrence</h4>
          <p className="text-sm text-violet-200">
            {summarizeRecurrence(
              form.recurrenceDays,
              // The effective date, not null — an unspecified end date is filled in
              // on submit, so reviewing must show what will actually be saved.
              form.recurrenceHasEndDate
                ? form.recurrenceEndDate
                : defaultRecurrenceEndDate(form.startTime, form.business?.timezone ?? "UTC"),
              form.startTime,
            )}
          </p>
        </div>
      )}
    </div>
  );
}
